import { Spinner } from '@fluentui/react-components';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import { STAFF_WAIT_PATH } from '@tvmf/shared';
import { useAuth } from './auth';
import { AppShell } from './components/AppShell';
import { RequirePermission } from './components/RequirePermission';
import { Login } from './pages/Login';
import { StaffAccessWait } from './pages/StaffAccessWait';
import { SetPassword } from './pages/SetPassword';
import { EnrolTotp } from './pages/EnrolTotp';
import { Dashboard } from './pages/Dashboard';
import { DataCollection } from './pages/DataCollection';
import { DataCollectionPolicies } from './pages/DataCollectionPolicies';
import { SiteWorkspace } from './pages/SiteWorkspace';
import { NumberPorting } from './pages/NumberPorting';
import { Build } from './pages/Build';
import { BuildSiteWorkspace } from './pages/BuildSiteWorkspace';
import { DeploymentSites } from './pages/DeploymentSites';
import { DeploymentSiteWorkspace } from './pages/DeploymentSiteWorkspace';
import { Files } from './pages/Files';
import { Handover } from './pages/Handover';
import { AdminUsers } from './pages/admin/Users';
import { AdminTenants } from './pages/admin/Tenants';
import { AdminMsps } from './pages/admin/Msps';
import { AdminSites } from './pages/admin/Sites';
import { AdminEmailLog } from './pages/admin/EmailLog';
import { AdminAudit } from './pages/admin/Audit';
import { AdminAgents } from './pages/admin/Agents';
import { AdminReleaseCheck } from './pages/admin/ReleaseCheck';
import { AdminPlatformSettings } from './pages/admin/PlatformSettings';
import { AdminSso } from './pages/admin/Sso';
import { FeatureRequests } from './pages/FeatureRequests';
import { MspIncidents } from './pages/MspIncidents';
import { MspServiceRequests } from './pages/MspServiceRequests';
import { IncidentNew } from './pages/IncidentNew';
import { IncidentPage } from './pages/IncidentPage';
import { IncidentResponse } from './pages/IncidentResponse';
import { Incidents } from './pages/Incidents';
import { Problems } from './pages/Problems';
import { ServiceRequestPage } from './pages/ServiceRequestPage';
import { ServiceRequests } from './pages/ServiceRequests';
import { BugReports } from './pages/BugReports';
import { Discovery } from './pages/Discovery';
import { MonitoringAlerts, MonitoringDevices, MonitoringWatchList } from './pages/Monitoring';

export function App() {
  const { status } = useAuth();
  const { pathname } = useLocation();

  // The "Is it fixed?" email links: answered signed out, so before any sign-in screen.
  if (pathname === '/incident-response') return <IncidentResponse />;
  // A Voxshift staff sign-in waiting for an administrator's approval (docs/SSO.md 4.3a): not signed in yet, the cookie from the sign-in says who is waiting.
  if (pathname === STAFF_WAIT_PATH) return <StaffAccessWait />;

  if (status === 'loading') {
    return (
      <div style={{ display: 'grid', placeItems: 'center', height: '100vh' }}>
        <Spinner label="Loading…" />
      </div>
    );
  }
  if (status === 'anonymous') return <Login />;
  if (status === 'pwreset') return <SetPassword />;
  if (status === 'enrol') return <EnrolTotp />;

  return (
    <AppShell>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/incidents" element={<Incidents />} />
        <Route path="/incidents/new" element={<IncidentNew />} />
        <Route
          path="/incidents/problems"
          element={
            <RequirePermission permission="inc:manage">
              <Problems />
            </RequirePermission>
          }
        />
        <Route path="/incidents/:id" element={<IncidentPage />} />
        <Route path="/data-collection" element={<DataCollection />} />
        <Route path="/data-collection/policies" element={<DataCollectionPolicies />} />
        <Route path="/data-collection/sites/:siteId" element={<SiteWorkspace />} />
        <Route path="/data-collection/sites/:siteId/number-porting" element={<NumberPorting />} />
        <Route
          path="/discovery"
          element={
            <RequirePermission permission="tenantdiscovery:read">
              <Discovery />
            </RequirePermission>
          }
        />
        <Route path="/monitoring/devices" element={<RequirePermission permission="monitoring:read"><MonitoringDevices /></RequirePermission>} />
        <Route path="/monitoring/add" element={<Navigate to="/monitoring/watch-list" replace />} />
        <Route path="/monitoring/watch-list" element={<RequirePermission permission="monitoring:manage"><MonitoringWatchList /></RequirePermission>} />
        <Route path="/monitoring/alerts" element={<RequirePermission permission="monitoring:manage"><MonitoringAlerts /></RequirePermission>} />
        <Route path="/monitoring" element={<Navigate to="/monitoring/devices" replace />} />
        <Route
          path="/build"
          element={
            <RequirePermission permission="build:read">
              <Build />
            </RequirePermission>
          }
        />
        <Route
          path="/build/sites/:siteId"
          element={
            <RequirePermission permission="build:read">
              <BuildSiteWorkspace />
            </RequirePermission>
          }
        />
        <Route
          path="/deployment"
          element={
            <RequirePermission permission="deployment:read">
              <DeploymentSites />
            </RequirePermission>
          }
        />
        <Route
          path="/deployment/sites/:siteId"
          element={
            <RequirePermission permission="deployment:read">
              <DeploymentSiteWorkspace />
            </RequirePermission>
          }
        />
        <Route path="/handover" element={<Handover />} />
        <Route
          path="/files"
          element={
            <RequirePermission permission="files:read">
              <Files />
            </RequirePermission>
          }
        />
        <Route
          path="/admin/users"
          element={
            <RequirePermission permission="user:read">
              <AdminUsers />
            </RequirePermission>
          }
        />
        <Route
          path="/admin/tenants"
          element={
            <RequirePermission permission="tenant:create">
              <AdminTenants />
            </RequirePermission>
          }
        />
        <Route
          path="/admin/msps"
          element={
            <RequirePermission permission="msp:read">
              <AdminMsps />
            </RequirePermission>
          }
        />
        <Route
          path="/admin/sites"
          element={
            <RequirePermission permission="discovery:sites:manage">
              <AdminSites />
            </RequirePermission>
          }
        />
        <Route
          path="/admin/release-check"
          element={
            <RequirePermission permission="platform:operate">
              <AdminReleaseCheck />
            </RequirePermission>
          }
        />
        <Route
          path="/admin/settings"
          element={
            <RequirePermission permission="platform:operate">
              <AdminPlatformSettings />
            </RequirePermission>
          }
        />
        <Route
          path="/admin/settings/sso"
          element={
            <RequirePermission permission="sso:manage">
              <AdminSso />
            </RequirePermission>
          }
        />
        <Route
          path="/admin/agents"
          element={
            <RequirePermission permission="agent:manage">
              <AdminAgents />
            </RequirePermission>
          }
        />
        <Route
          path="/admin/email"
          element={
            <RequirePermission permission="audit:read:platform">
              <AdminEmailLog />
            </RequirePermission>
          }
        />
        <Route
          path="/admin/audit"
          element={
            <RequirePermission permission="audit:read:platform">
              <AdminAudit />
            </RequirePermission>
          }
        />
        <Route
          path="/feature-requests"
          element={
            <RequirePermission permission="feature:read">
              <FeatureRequests />
            </RequirePermission>
          }
        />
        <Route
          path="/msp/incidents"
          element={
            <RequirePermission permission="inc:msp">
              <MspIncidents />
            </RequirePermission>
          }
        />
        <Route
          path="/msp/service-requests"
          element={
            <RequirePermission permission="sr:msp">
              <MspServiceRequests />
            </RequirePermission>
          }
        />
        <Route
          path="/service-requests"
          element={
            <RequirePermission permission="sr:read">
              <ServiceRequests />
            </RequirePermission>
          }
        />
        <Route
          path="/service-requests/:id"
          element={
            <RequirePermission permission="sr:read">
              <ServiceRequestPage />
            </RequirePermission>
          }
        />
        <Route
          path="/bug-reports"
          element={
            <RequirePermission permission="bug:read">
              <BugReports />
            </RequirePermission>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppShell>
  );
}
